import { type GetOutput, type GetParams, jwApi } from '@jwplayer/companion-gateway';
import { queryCache, useMutation } from 'react-query';

import { useQuery } from '/module/query';

type T = Partial<GetOutput<'sites/content-protection', 'read'>>;

const getQueryKey = (siteId: string) => ['site-content-protection', siteId];

const retrieve = async (siteId: string, signal?: AbortController['signal']): Promise<T> =>
  (await jwApi('sites/content-protection', 'read', { routeParams: { siteId } }, { signal })).data;

export const hydrateQueryCache = async (siteId: string) => {
  await queryCache.prefetchQuery(getQueryKey(siteId), ({ signal }) => retrieve(siteId, signal));
};

export const useSiteContentProtectionSettings = (siteId: string) => {
  const queryKey = getQueryKey(siteId);

  const initialData = queryCache.getQueryData<T>(queryKey);

  const query = useQuery(queryKey, (): Promise<T> => retrieve(siteId), {
    forceFetchOnMount: false,
    refetchOnMount: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    initialData,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const mutation = useMutation(
    async (input: GetParams<'sites/content-protection', 'update'>['input']): Promise<T> => {
      return (
        await jwApi('sites/content-protection', 'update', {
          routeParams: {
            siteId,
          },
          input,
        })
      ).data;
    },
    {
      onSuccess(data) {
        // Ensure that our local cache is updated to the latest information
        const existingData = queryCache.getQueryData<T>(queryKey);
        queryCache.setQueryData<T>(queryKey, { ...existingData, ...data });
      },
    },
  );

  return {
    query,
    mutation,
  };
};
